/**
 * Reusable component to be imported for blocks which allow for inline and/or modal playback.
 * Supports both <video> and <iframe> embeds.
 *
 * Expected markup:
 * - Inline: container has a `.js-play-button` and a media element (video/iframe) hidden until played.
 * - Modal: trigger uses `.js-modal-trigger` and content lives in `.js-modal-content`.
 *
 * @param {HTMLElement[]} blocks - An array of blocks.
 */
export const playbackEnabledBlock = (blocks) => {
    const getMediaElement = (block) => {
        return block.querySelector('video[data-src]') || block.querySelector('iframe[data-src]')
    }

    blocks.forEach((block) => {
        // Check if media exists first.
        const media = getMediaElement(block)
        if (!media) return

        // Get the playback type which will determine whether we're loading inline or in a modal.
        // If no value then set 'inline' to be the default.
        let { playbackType } = media.dataset
        if (!playbackType) playbackType = 'inline'

        if (playbackType === 'inline') {
            // Inline playback.
            import('./inlinePlayback.js').then((module) => {
                module.inlinePlayback(block, media)
            })

            // Stop playback when the block leaves the viewport.
            import('./videoLeftViewport.js').then((module) => {
                module.videoLeftViewport(block, media)
            })

            return
        }

        // Modal playback.
        import('../../components/modal.js').then((module) => {
            module.modalInit(block, {
                onShow: (modal) => {
                    const wrapper = modal.querySelector('.js-modal-content-wrapper')
                    if (!wrapper) return

                    const modalMedia =
                        wrapper.querySelector('video') || wrapper.querySelector('iframe')

                    if (!modalMedia) return

                    // Lazyload media by setting the source.
                    import('./setMediaSource.js').then((sourceModule) => {
                        sourceModule
                            .setMediaSource(modalMedia)
                            .then(() => {
                                const tag = modalMedia.tagName.toLowerCase()

                                if (tag === 'video') {
                                    modalMedia.setAttribute('autoplay', '')
                                    modalMedia
                                        .play()
                                        .catch((err) => console.warn('Video failed to play: ', err))
                                }

                                modalMedia.setAttribute('tabindex', '-1')
                                modalMedia.focus()
                            })
                            .catch((err) => console.warn('Failed to load media source: ', err))
                    })
                },
            })
        })
    })
}
